/**
 * THE PEOPLE — a searchable list, and one person whole.
 *
 * The detail page is where "all the capability, none of the button bloat"
 * gets tested. Everything about a customer is reachable from here: their
 * cars, their work, their bills. None of it is a top-level nav item,
 * because none of it is a place you go — they are things you look at
 * *about somebody*, and the way to them is through the somebody.
 */

import { fleetCard } from "./fleetcard.ts";
import { listCustomers, getCustomer, updateCustomerField, type CustomerRow } from "../data/customers.ts";
import { canEdit, whyNot, type Role, type Field } from "../domain/permissions.ts";
import { editable, bindEditables } from "../ui/editable.ts";
import { STATUS_LABEL, type Status } from "../data/jobs.ts";
import { esc, money, ago, mount, since } from "../ui/dom.ts";
import { db, api, configNow } from "../data/client.ts";

/** Hidden entirely where the deployment cannot text — a dead button is worse. */
const canText = (): boolean => {
  try { return configNow().features.sms; } catch { return false; }
};

/**
 * A text, typed and sent by a person.
 *
 * Deliberately not a thread view and deliberately not automated. The old
 * system's loops texted one customer 203 times in a night because nobody
 * had to press anything; this sends exactly once, when somebody means it.
 */
/**
 * An email, typed and sent by a person. Same shape as the text, because
 * the shop should not have to learn two things — and same rule underneath:
 * the address comes from the record, never from this box.
 */
function wireEmail(customerId: string, name: string, after: () => void): void {
  document.getElementById("eml-open")?.addEventListener("click", () => {
    const out = document.getElementById("eml-out");
    if (!out) return;
    if (out.innerHTML) { out.innerHTML = ""; return; }
    out.innerHTML = `
      <div class="txtbox">
        <label class="field"><span>Subject</span><input id="eml-subject" maxlength="120"></label>
        <label class="field"><span>Message</span><textarea id="eml-body" rows="4" maxlength="4000"></textarea></label>
        <div class="txt-act">
          <button class="btn primary sm" id="eml-send">Send it</button>
          <span class="fine" id="eml-msg">Goes from the shop's address and lands in their history.</span>
        </div>
      </div>`;

    document.getElementById("eml-send")?.addEventListener("click", async () => {
      const b = document.getElementById("eml-send") as HTMLButtonElement;
      const msg = document.getElementById("eml-msg")!;
      const subject = (document.getElementById("eml-subject") as HTMLInputElement).value.trim();
      const text = (document.getElementById("eml-body") as HTMLTextAreaElement).value.trim();
      if (!subject) { msg.textContent = "Give it a subject."; return; }
      if (!text) { msg.textContent = "Nothing to send yet."; return; }

      b.disabled = true; b.textContent = "Sending…";
      try {
        const sb = await db();
        const { data: { session } } = await sb.auth.getSession();
        const res = await fetch(api("/api/email/send"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session?.access_token ?? ""}`,
          },
          body: JSON.stringify({ customer_id: customerId, subject, message: text }),
        });
        const body = await res.json() as { ok?: boolean; error?: string };
        if (!res.ok || !body.ok) {
          b.disabled = false; b.textContent = "Send it";
          msg.textContent = body.error ?? "That did not go.";
          return;
        }
        out.innerHTML = `<p class="msg ok">Sent.</p>`;
        after();
      } catch (e) {
        b.disabled = false; b.textContent = "Send it";
        msg.textContent = e instanceof Error ? e.message : String(e);
      }
    });
  });
}

function wireText(customerId: string, name: string, after: () => void): void {
  document.getElementById("txt-open")?.addEventListener("click", () => {
    const out = document.getElementById("txt-out");
    if (!out) return;
    if (out.innerHTML) { out.innerHTML = ""; return; }
    out.innerHTML = `
      <div class="txtbox">
        <label class="field"><span>Message</span><textarea id="txt-body" rows="3" maxlength="1200"></textarea></label>
        <div class="txt-act">
          <button class="btn primary sm" id="txt-send">Send it</button>
          <span class="fine" id="txt-msg">Goes from the shop's number. It lands in their thread.</span>
        </div>
      </div>`;

    document.getElementById("txt-send")?.addEventListener("click", async () => {
      const b = document.getElementById("txt-send") as HTMLButtonElement;
      const msg = document.getElementById("txt-msg")!;
      const text = (document.getElementById("txt-body") as HTMLTextAreaElement).value.trim();
      if (!text) { msg.textContent = "Nothing to send yet."; return; }

      b.disabled = true; b.textContent = "Sending…";
      try {
        const sb = await db();
        const { data: { session } } = await sb.auth.getSession();
        const res = await fetch(api("/api/sms/send"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session?.access_token ?? ""}`,
          },
          body: JSON.stringify({ customer_id: customerId, message: text }),
        });
        const body = await res.json() as { ok?: boolean; error?: string; warning?: string };
        if (!res.ok || !body.ok) {
          b.disabled = false; b.textContent = "Send it";
          msg.textContent = body.error ?? "That did not go.";
          return;
        }
        out.innerHTML = `<p class="msg ok">Sent${body.warning ? ` — ${esc(body.warning)}` : ""}.</p>`;
        after();
      } catch (e) {
        b.disabled = false; b.textContent = "Send it";
        msg.textContent = e instanceof Error ? e.message : String(e);
      }
    });
  });
}
import { picker, sortsFor, orderFor, type SortKey } from "../ui/picker.ts";
import { customerComms } from "./comms.ts";
import { notesCard } from "./notes.ts";
import { newJobSheet } from "./newjob.ts";
import { openCustomerDrawer } from "./customerdrawer.ts";
import { drawerMode } from "../ui/drawer.ts";
import { shopWords } from "../ui/words.ts";
import { cap } from "../domain/trade.ts";

const PAGE = 50;
let state = { search: "", offset: 0 };
let sort: SortKey = "new";

function row(c: CustomerRow): string {
  return `
    <a class="row" href="#/customer/${c.id}">
      <div class="row-main">
        <div class="row-title"><span class="num">${esc(c.full_name)}</span></div>
        <div class="row-sub">${esc(c.phone ?? "no phone")}${
          c.address ? `<span class="dot">&middot;</span>${esc(c.address)}` : ""}</div>
      </div>
      <!-- WHEN WE LAST DEALT WITH THEM, in minutes if it was minutes. The
           row used to show the day they were added to the file, which for
           an imported shop is the same day for three and a half thousand
           people. -->
      <div class="row-side"><div class="when">${
        esc(since(c.updated_at ?? c.created_at))}</div></div>
    </a>`;
}

export async function customersPage(): Promise<void> {
  mount(`<div class="page">
      <header class="page-head"><h1>Customers</h1><p>Search by name, phone, email or address.</p>
      <!-- A new customer with no job is a row nobody looks at, so the one
           button makes both — the shop's unit of work is a job, not a
           contact. -->
      <div class="headacts">
        <button class="btn primary newjob" id="newcust">Add a customer</button>
        <button class="btn newjob" id="newjob">Start a job</button>
      </div></header>
      <div class="searchbar">
        <label class="field"><span>Search</span><input id="cust-q" type="search" value="${esc(state.search)}" autocomplete="off"></label>
      </div>
      <div class="listbar" id="cust-sort"></div>
      <div class="card" id="cust-list"><div class="loading">Reading&hellip;</div></div>
      <div class="pager" id="cust-pager"></div>
    </div>`);

  const p = picker(sortsFor(["new", "old", "az"]), sort, { label: "Sort" });
  const bar = document.getElementById("cust-sort");
  if (bar) bar.innerHTML = p.html;
  p.bind((k) => { sort = k; state.offset = 0; void draw(); });

  document.getElementById("newcust")?.addEventListener("click", () =>
    newJobSheet((id) => { location.hash = `#/customer/${id}`; }, "customer"));

  document.getElementById("newjob")?.addEventListener("click", () =>
    newJobSheet((jobId) => { location.hash = `#/job/${jobId}`; }));

  const input = document.getElementById("cust-q") as HTMLInputElement | null;
  input?.focus();

  // Typing is not a query. Waiting a beat after somebody stops is.
  let t: ReturnType<typeof setTimeout>;
  input?.addEventListener("input", () => {
    clearTimeout(t);
    t = setTimeout(() => { state = { search: input.value, offset: 0 }; void draw(); }, 220);
  });

  // Tap a customer and the drawer slides in over the list, rather than
  // navigating away — the list keeps its place and its scroll. The row is
  // still a real link (#/customer/:id) so a middle-click, a long-press or a
  // no-JS load all still work; we only take over the plain left click.
  // Delegated on the container, which outlives each redraw.
  // Off a full-screen desktop there is no list left beside the drawer to
  // keep your place in, so the quick look stops being a quick look and the
  // row goes back to being what the markup already says it is: a link to
  // the customer. One rule, read from ui/drawer.ts, so the panel and this
  // cannot disagree about what a small window means.
  document.getElementById("cust-list")?.addEventListener("click", (e) => {
    const ev = e as MouseEvent;
    if (!drawerMode()) return;
    if (ev.button !== 0 || ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) return;
    const link = (ev.target as HTMLElement).closest<HTMLAnchorElement>('a.row[href^="#/customer/"]');
    if (!link) return;
    const id = link.getAttribute("href")?.split("/").pop();
    if (!id) return;
    ev.preventDefault();
    void openCustomerDrawer(id);
  });

  await draw();
}

async function draw(): Promise<void> {
  const host = document.getElementById("cust-list");
  const pager = document.getElementById("cust-pager");
  if (!host) return;
  try {
    const o = orderFor(sort, { date: "updated_at", name: "full_name" });
    const { rows, total } = await listCustomers({
      search: state.search, limit: PAGE, offset: state.offset,
      orderColumn: o.column, orderAscending: o.ascending,
    });
    host.innerHTML = rows.length
      ? rows.map(row).join("")
      : `<div class="empty">Nobody matches ${state.search ? `&ldquo;${esc(state.search)}&rdquo;` : "that"}.</div>`;

    if (pager) {
      const from = total ? state.offset + 1 : 0;
      const to = Math.min(state.offset + PAGE, total);
      pager.innerHTML = `
        <button class="btn sm" id="prev" ${state.offset === 0 ? "disabled" : ""}>&larr; Back</button>
        <span>${from}&ndash;${to} of ${total.toLocaleString()}</span>
        <button class="btn sm" id="next" ${to >= total ? "disabled" : ""}>More &rarr;</button>`;
      document.getElementById("prev")?.addEventListener("click", () => {
        state.offset = Math.max(0, state.offset - PAGE); void draw();
      });
      document.getElementById("next")?.addEventListener("click", () => {
        state.offset += PAGE; void draw();
      });
    }
  } catch (e) {
    host.innerHTML = `<div class="empty bad">${esc(e instanceof Error ? e.message : String(e))}</div>`;
  }
}

export async function customerPage(id: string, role: Role = "viewer"): Promise<void> {
  mount(`<div class="page"><div class="loading">Reading&hellip;</div></div>`);
  let whole;
  try { whole = await getCustomer(id); }
  catch (e) {
    mount(`<div class="page"><div class="empty bad">${esc(e instanceof Error ? e.message : String(e))}</div></div>`);
    return;
  }
  if (!whole) { mount(`<div class="page"><div class="empty">That customer is not here.</div></div>`); return; }

  const { customer: c, vehicles, jobs, invoices } = whole;
  const car = (v: (typeof vehicles)[number]) =>
    [v.year, v.make, v.model].filter(Boolean).join(" ").trim() || v.description || shopWords().unidentified;

  /** Every field on this page goes through here, so the rule is applied once. */
  const f = (column: string, value: string | null, placeholder: string, multiline = false) => {
    const key = `customer.${column}` as Field;
    return editable({
      table: "customers", id, column, value, placeholder, multiline,
      editable: canEdit(role, key), reason: whyNot(role, key),
      save: (next) => updateCustomerField(id, column, next),
    });
  };

  mount(`
    <div class="page">
      <header class="page-head">
        <a class="back" href="#/customers">&larr; Customers</a>
        <h1>${f("full_name", c.full_name, "Name")}</h1>
        <p>${c.source ? `came in via ${esc(c.source)}` : "&nbsp;"}</p>
      </header>

      <div class="grid2">
        <div class="card pad">
          <div class="kv"><span>Phone</span><b>${f("phone", c.phone, "Add a number")}
            ${c.phone ? `<a class="navlink" href="tel:${esc(c.phone)}">call</a>
              ${canText() ? `<button class="navlink" id="txt-open">text</button>` : ""}` : ""}</b></div>
          <div id="txt-out"></div>
          <div class="kv"><span>Email</span><b>${f("email", c.email, "Add an email")}
            ${c.email ? `<button class="navlink" id="eml-open">email</button>` : ""}</b></div>
          <div id="eml-out"></div>
          <div class="kv"><span>Address</span><b>${f("address", c.address, "Add an address")}
            ${c.address ? `<a class="navlink" target="_blank" rel="noopener"
              href="https://www.google.com/maps/dir/?api=1&destination=${
                encodeURIComponent(c.address)}">navigate</a>` : ""}</b></div>
          <div class="kv"><span>Notes</span><b>${f("notes", c.notes, "Add a note", true)}</b></div>
          <div class="kv"><span>On file since</span><b>${esc(since(c.created_at))}</b></div>
        </div>
        <div class="card pad">
          <div class="card-sub">${esc(cap(shopWords().things))}</div>
          ${vehicles.length ? vehicles.map((v) => `
            <div class="kv"><span>${esc(car(v))}</span><b>${esc(shopWords().hasPlate ? (v.license_plate ?? "") : "")}</b></div>`).join("")
            : `<div class="quiet">No ${esc(shopWords().thing)} on file.</div>`}
        </div>
      </div>

      <div class="card" id="cust-fleet"></div>

      <div class="card">
        <div class="card-head">Work &middot; ${jobs.length}</div>
        ${jobs.length ? jobs.map((j) => `
          <a class="row" href="#/job/${j.id}">
            <div class="row-main">
              <div class="row-title"><span class="num">${esc(j.job_number)}</span>
                <span class="pill s-${esc(j.status)}">${esc(STATUS_LABEL[j.status as Status] ?? j.status)}</span></div>
              ${j.title ? `<div class="row-sub">${esc(j.title)}</div>` : ""}
            </div>
            <div class="row-side"><div class="when">${esc(since(j.created_at))}</div></div>
          </a>`).join("") : `<div class="empty">No work yet.</div>`}
      </div>

      <div class="card" id="cust-notes"></div>

      <div class="card" id="cust-comms"><div class="loading">Reading&hellip;</div></div>

      <div class="card">
        <div class="card-head">Bills &middot; ${invoices.length}</div>
        ${invoices.length ? invoices.map((i) => `
          <div class="row">
            <div class="row-main">
              <div class="row-title"><span class="num">${esc(i.invoice_number)}</span>
                <span class="pill s-${i.status === "paid" ? "paid" : "estimate"}">${esc(i.status)}</span></div>
            </div>
            <div class="row-side"><div class="amount">${money(i.total)}</div>
              <div class="when">${esc(since(i.issued_at))}</div></div>
          </div>`).join("") : `<div class="empty">Nothing billed yet.</div>`}
      </div>
    </div>`);

  bindEditables();
  // The fleet dashboard switch and link, 0213. See pages/fleetcard.ts.
  /* Not on a case file: a fleet is vehicles (Jev's audit loop, 2026-10-14). */
  if (!shopWords().caseFile) void fleetCard("cust-fleet", id, role);
  /* Notes about the PERSON, across every car they have ever brought in.
     The old single `notes` field above stays where it is and still holds
     what people wrote into it; this is the one with an author and a
     time on each entry. */
  void notesCard("cust-notes", { customerId: id }, role, {
    title: "Notes", blurb: "Anything worth knowing next time they call" });
  wireText(id, c.full_name, () => void customerComms("cust-comms", id, c.phone));
  wireEmail(id, c.full_name, () => void customerComms("cust-comms", id, c.phone));
  void customerComms("cust-comms", id, c.phone);
}
